import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, FormField, FormHelp, Input, InputChip, Label, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyUnitAmenityWebApiHandler_create,
  useRentalManagerUserPropertyUnitAmenityWebApiHandler_delete,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { CharacterCountFormHelp } from '@zg-rentals/visual-cohesion';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../MFAccordion/utils';

import { useAuth } from '../../AuthProvider';
import VisuallyHiddenScreenReader from '../../VisuallyHiddenScreenReader';

import type { ChangeEvent } from 'react';

const ENTER = 13;

type Props = {
  floorPlanIndex: number;
};

const FloorPlanAmenities = ({ floorPlanIndex }: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails, isFetching: isFetchingListingDetails } = useGetListingDetails(listingAlias);

  const [amenityText, setAmenityText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const MAX_LENGTH = 100;
  const isOverLimit = amenityText.length > MAX_LENGTH;

  const floorplans = listingDetails?.floorPlans || [];
  const floorPlan = floorplans[floorPlanIndex];
  const canUpdateListing = user?.features?.canUpdateListing ?? false;
  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: createUnitAmenity, isPending: isCreatingUnitAmenity } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyUnitAmenityWebApiHandler_create,
    skipToast: true,
  });

  const { mutateAsync: deleteUnitAmenity, isPending: isDeletingUnitAmenity } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyUnitAmenityWebApiHandler_delete,
    skipToast: true,
  });

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setAmenityText(event.target.value);
    setErrorMessage('');
  };

  const handleOnBlur = async () => {
    setIsFocused(false);
    setSuccessMessage('');

    const unitAmenities = floorPlan?.unitAmenities || [];
    const tagsMap = unitAmenities.reduce((acc: Record<string, boolean>, curVal: { amenity: string }) => {
      acc[curVal.amenity.toLowerCase()] = true;
      return acc;
    }, {});

    const valueToSave = amenityText.trim();

    if (valueToSave.length === 0 || isOverLimit) {
      return;
    }

    if (tagsMap[valueToSave.toLowerCase()]) {
      setErrorMessage('You’ve already entered this tag');
      return;
    }

    const floorPlanId = floorPlan?.id;

    if (!floorPlanId) {
      enqueueToast(<Toast tone="critical">Something went wrong, refresh the page and try again</Toast>);
      return;
    }

    try {
      await createUnitAmenity({
        getParams: {
          floorPlanId,
          text: valueToSave,
        },
      });

      setSuccessMessage(`Added ${amenityText}.`);
      setAmenityText('');
      setErrorMessage('');
    } catch (error) {
      const err = error as { error?: { message?: string } };
      if (err?.error?.message?.includes('Good Neighbor Policy')) {
        setErrorMessage(err.error.message);
      } else {
        enqueueToast(
          <Toast tone="critical">
            {err?.error?.message || 'There was an error creating the amenity, refresh the page and try again'}
          </Toast>,
        );
      }
    }
  };

  const handelKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    setErrorMessage('');

    if (event.keyCode === ENTER) {
      event.preventDefault();
      handleOnBlur();
    }
  };

  const onTagClose = async (amenityIndex: number, amenityId: string) => {
    setSuccessMessage('');

    const unitAmenities = floorPlan?.unitAmenities || [];
    const amenityValueToDelete = unitAmenities[amenityIndex]?.amenity;

    try {
      await deleteUnitAmenity({ getParams: { amenityId } });
      setSuccessMessage(`Deleted ${amenityValueToDelete}.`);
    } catch (error) {
      enqueueToast(
        <Toast tone="critical">
          {error?.message || 'There was an error deleting the amenity, refresh the page and try again'}
        </Toast>,
      );
    }
  };

  return (
    <Box
      data-testid="FloorPlanAmenities"
      css={{ display: 'flex', flexDirection: 'column', gap: '400', flex: '1 0 280px' }}
    >
      <Box css={{ display: 'flex', flexDirection: 'row', gap: '400', alignItems: 'flex-start' }}>
        <Box css={{ flex: { xl: 'auto', base: '0 1 420px' } }}>
          <FormField
            error={!!errorMessage || isOverLimit}
            disabled={isInputFormDisabled || isCreatingUnitAmenity || isDeletingUnitAmenity || isFetchingListingDetails}
            label={<Label>Floor plan amenities</Label>}
            control={
              <Input
                name="amenityText"
                value={amenityText}
                onChange={handleOnChange}
                onFocus={() => setIsFocused(true)}
                onBlur={handleOnBlur}
                onKeyDown={handelKeyPress}
              />
            }
            description={
              errorMessage ? (
                <FormHelp>{errorMessage}</FormHelp>
              ) : isFocused || isOverLimit ? (
                <CharacterCountFormHelp
                  currentLength={amenityText.length}
                  maxLength={MAX_LENGTH}
                  isFocused={isFocused}
                  label="Amenity"
                />
              ) : null
            }
          />
        </Box>

        <Button
          tone="brand"
          emphasis="filled"
          loading={isCreatingUnitAmenity || isDeletingUnitAmenity || isFetchingListingDetails}
          disabled={isInputFormDisabled || isOverLimit}
          css={{ marginTop: '600' }}
        >
          Add
        </Button>
      </Box>

      {floorPlan?.unitAmenities && floorPlan.unitAmenities.length > 0 && (
        <Box
          css={{
            display: 'flex',
            gap: '200',
            flexWrap: 'wrap',
            opacity: isInputFormDisabled ? 'action.disabled' : 'action.active',
          }}
        >
          {floorPlan.unitAmenities.map(({ id, amenity }: { id: string; amenity: string }, index: number) => (
            <InputChip
              key={`FloorPlanAmenities-Tag-${amenity}`}
              disabled={isInputFormDisabled}
              isOpen={!isInputFormDisabled}
              onClose={() => onTagClose(index, id)}
            >
              {amenity}
            </InputChip>
          ))}
        </Box>
      )}

      <VisuallyHiddenScreenReader errorMessage={errorMessage} successMessage={successMessage} />
    </Box>
  );
};

export default memo(FloorPlanAmenities);
